/**
 * One-click fixes for the board and position-plan checks (docs/decisions/0014): record the
 * blanking plates, add a north arrow, draw the cable routes from the board to the points. (The
 * phase balance is computed by `balancedPhases` in @elec/calc.)
 */
import { newId, type BlankingStatus, type Element, type Id, type Op, type Project } from '@elec/domain';
import { analyse, type Analysis } from '@elec/analysis';
import { pageLayout } from '@elec/layout';
import { elementBounds, getSymbol, portPosition } from '@elec/symbols';

/**
 * Blanking plates of the board drawn on a single-line sheet (undefined: not checked yet). A
 * sheet generated from the site survey keeps it on its survey board too, so generating the
 * diagrams again keeps it.
 */
export function blankingOps(project: Project, sheetId: Id, status: BlankingStatus | undefined): Op[] {
  const s = project.sheets[sheetId];
  if (!s) return [];
  const ops: Op[] = [];
  if (s.blankingPlates !== status)
    ops.push({ op: 'update', coll: 'sheets', id: sheetId, patch: { blankingPlates: status } });
  const board = s.source?.kind === 'survey' ? project.surveyBoards?.[s.source.fromSheetId] : undefined;
  if (board && board.blankingPlates !== status)
    ops.push({ op: 'update', coll: 'surveyBoards', id: board.id, patch: { blankingPlates: status } });
  return ops;
}

/** The same from the survey board: on the board and on the single-line sheet generated from it. */
export function surveyBlankingOps(project: Project, boardId: Id, status: BlankingStatus | undefined): Op[] {
  const board = project.surveyBoards?.[boardId];
  if (!board) return [];
  const ops: Op[] =
    board.blankingPlates === status
      ? []
      : [{ op: 'update', coll: 'surveyBoards', id: boardId, patch: { blankingPlates: status } }];
  for (const s of Object.values(project.sheets))
    if (s.kind === 'single-line' && s.source?.kind === 'survey' && s.source.fromSheetId === boardId)
      if (s.blankingPlates !== status)
        ops.push({ op: 'update', coll: 'sheets', id: s.id, patch: { blankingPlates: status } });
  return ops;
}

/** A north arrow in the top right corner of a position plan, pointing up (angle 0). */
export function northArrowOps(project: Project, sheetId: Id): { ops: Op[]; id: Id } {
  const s = project.sheets[sheetId];
  const id = newId();
  if (!s) return { ops: [], id };
  const { content } = pageLayout(project, s);
  return {
    id,
    ops: [
      {
        op: 'add',
        coll: 'elements',
        value: {
          id,
          sheetId,
          symbolId: 'plan.north',
          x: Math.round((content.maxX - 25) / 2.5) * 2.5,
          y: Math.round((content.minY + 5) / 2.5) * 2.5,
          rotation: 0,
          mirrored: false,
          tag: '',
          props: { angle: 0 },
        },
      },
    ],
  };
}

const centre = (e: Element) => {
  const b = elementBounds(getSymbol(e.symbolId)!, e);
  return { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 };
};

/**
 * Cable routes on a position plan: for each circuit with points on it, from the board to the
 * nearest point, then on to the next nearest, as right-angled lines along the walls (the path
 * the length estimate takes). The first line of each circuit carries its letter and cable. Draft
 * routes, for the user to move onto the real ones. Circuits already routed are left alone.
 */
export function cableRoutesOps(project: Project, planSheetId: Id, a: Analysis = analyse(project)): Op[] {
  const on = Object.values(project.elements).filter((e) => e.sheetId === planSheetId);
  const board = on.find((e) => e.symbolId === 'iec.board');
  if (!board) return [];
  const wires = Object.values(project.wires).filter((w) => w.sheetId === planSheetId);
  const wired = new Set(
    wires.flatMap((w) => [w.from, w.to]).flatMap((e) => (e.kind === 'port' ? [e.elementId] : [])),
  );
  const end = (e: Element) => {
    const def = getSymbol(e.symbolId)!;
    const port = def.ports[0];
    return port
      ? { end: { kind: 'port' as const, elementId: e.id, portId: port.id }, at: portPosition(def, e, port) }
      : { end: { kind: 'point' as const, ...centre(e) }, at: centre(e) };
  };
  const ops: Op[] = [];
  for (const c of a.circuits) {
    const tags = new Set(c.points.map((p) => p.element.tag).filter(Boolean));
    const pts = on.filter((e) => tags.has(e.tag) && e.id !== board.id);
    if (!pts.length || pts.some((e) => wired.has(e.id))) continue;
    let from = end(board);
    const left = [...pts];
    let first = true;
    while (left.length) {
      left.sort((x, y) => {
        const d = (e: Element) => Math.abs(centre(e).x - from.at.x) + Math.abs(centre(e).y - from.at.y);
        return d(x) - d(y);
      });
      const to = end(left.shift()!);
      const via =
        Math.abs(to.at.x - from.at.x) > 0.1 && Math.abs(to.at.y - from.at.y) > 0.1
          ? [{ x: to.at.x, y: from.at.y }]
          : [];
      const cable =
        c.cable.sectionMm2 > 0
          ? ` · ${c.cable.type} ${c.cable.cores}G${String(c.cable.sectionMm2).replace('.', ',')}`.replace(
              '  ',
              ' ',
            )
          : '';
      ops.push({
        op: 'add',
        coll: 'wires',
        value: {
          id: newId(),
          sheetId: planSheetId,
          from: from.end,
          to: to.end,
          via,
          ...(first && c.code ? { label: `${c.code}${cable}` } : {}),
        },
      });
      first = false;
      from = to;
    }
  }
  return ops;
}
